import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputNumber } from "primereact/inputnumber";
import { InputTextarea } from "primereact/inputtextarea";
import { IContractType } from "store/queries/contractFeeSchedules";

import {
  CONTRACT_TYPE_OPTIONS,
  MULTIPLE_PROCEDURE_METHODOLOGY,
  reimbursementFieldsFor,
} from "../helpers";

export interface IContractFacts {
  contractType?: IContractType | null;
  signedDate?: Date | null;
  timelyFilingDays?: number | null;
  inpatientReimbursement?: string;
  outpatientReimbursement?: string;
  facilityReimbursement?: string;
  nonFacilityReimbursement?: string;
  /** CLE-2811 (2026-09-28) — the MPPR methodology as the contract states it. Informational only. */
  multipleProcedureMethodology?: string;
}

interface IContractFactsFieldsProps {
  values: IContractFacts;
  onChange: (field: keyof IContractFacts, value: unknown) => void;
  /** Grid classes per field, so Create (2 cols) and Edit (3 cols) lay out alike. */
  fieldClassName?: string;
}

/**
 * CLE-2811 — the contract facts a contract manager asks about: what kind of contract it is, when it
 * was signed, the filing deadline, and how the payer actually pays.
 *
 * Two deliberate behaviours:
 *
 *  1. **The reimbursement boxes follow the contract type.** A surgery center has one schedule, a
 *     hospital has inpatient and outpatient, a physician group has facility and non-facility. Showing
 *     all four on every contract invites someone to fill "inpatient" on an ASC, so nothing renders
 *     until a type is chosen.
 *  2. **Signed date sits next to the fee effective date and says so.** These two are confused
 *     constantly — the effective date moves with every amendment, the signed date does not — so the
 *     helper text names the difference rather than assuming the label carries it.
 *
 * None of these fields price anything. They are a record of what was negotiated.
 */
export function ContractFactsFields({
  values,
  onChange,
  fieldClassName = "col-12 md:col-6",
}: IContractFactsFieldsProps) {
  const reimbursementFields = reimbursementFieldsFor(values.contractType);

  return (
    <>
      <div className={fieldClassName}>
        <label className="block text-900 font-medium mb-2">Contract Type</label>
        <Dropdown
          value={values.contractType ?? null}
          options={CONTRACT_TYPE_OPTIONS}
          onChange={(e) => onChange("contractType", e.value ?? null)}
          placeholder="Select Contract Type"
          className="w-full"
          showClear
        />
        <small className="text-600 mt-1 block">
          Decides which reimbursement fields apply below.
        </small>
      </div>

      <div className={fieldClassName}>
        <label className="block text-900 font-medium mb-2">Signed Date</label>
        <Calendar
          value={values.signedDate ?? null}
          onChange={(e) => onChange("signedDate", e.value ?? null)}
          placeholder="Select Signed Date"
          className="w-full"
          showIcon
        />
        <small className="text-600 mt-1 block">
          When the agreement was executed — not the fee effective date, which
          moves with every amendment.
        </small>
      </div>

      <div className={fieldClassName}>
        <label className="block text-900 font-medium mb-2">
          Timely Filing (days)
        </label>
        <InputNumber
          value={values.timelyFilingDays ?? null}
          onValueChange={(e) => onChange("timelyFilingDays", e.value ?? null)}
          placeholder="e.g. 90"
          className="w-full"
          min={0}
          useGrouping={false}
        />
        <small className="text-600 mt-1 block">
          Days from date of service to submit a claim.
        </small>
      </div>

      {reimbursementFields.map((field) => (
        <div className="col-12" key={field.key}>
          <label className="block text-900 font-medium mb-2">
            {field.label}
          </label>
          <InputTextarea
            value={(values[field.key as keyof IContractFacts] as string) || ""}
            onChange={(e) =>
              onChange(field.key as keyof IContractFacts, e.target.value)
            }
            placeholder="How the payer pays for this — e.g. DRG base rate $8,400, outlier threshold $52,000"
            rows={2}
            className="w-full"
            autoResize
          />
        </div>
      ))}

      {/* CLE-2811 (2026-09-28) — every contract type has one. The AI modeling tool's save fills it (it
          used to append it to Notes). A record of what was negotiated: MPPR pricing is the adjustment
          rules' job, not this box's. */}
      <div className="col-12">
        <label
          htmlFor="contract-mppr-methodology"
          className="block text-900 font-medium mb-2"
        >
          {MULTIPLE_PROCEDURE_METHODOLOGY}
        </label>
        <InputTextarea
          id="contract-mppr-methodology"
          value={values.multipleProcedureMethodology || ""}
          onChange={(e) =>
            onChange("multipleProcedureMethodology", e.target.value)
          }
          placeholder="As the contract states it — e.g. 100/50/25/0, or 100% primary, 50% each additional"
          rows={2}
          className="w-full"
          autoResize
        />
        <small className="text-600 mt-1 block">
          Recorded only. Multiple-procedure pricing comes from the adjustment
          rules.
        </small>
      </div>
    </>
  );
}

export default ContractFactsFields;
